import { validationLabels } from "../core/l10n.js";

const validationForm = (() => {
  const lang = document.documentElement.lang || "fr";
  const PLACEHOLDER_INPUT = "";
  const DEFAULT_MIN_VALUE = 0;
  const DEFAULT_MAX_VALUE = 9999999;
  const MAX_BRANDS_ALLOWED = 5;

  let formIsValid = false;
  let itemsValidated = [];

  /**
   * Validates min/max value ranges
   * @param {string|number} min - Minimum value
   * @param {string|number} max - Maximum value
   * @returns {Object} Validation result with isValid flag and message
   */
  const minMax = (min, max) => {
    const minValue = Number(min) || DEFAULT_MIN_VALUE;
    const maxValue = Number(max) || DEFAULT_MAX_VALUE;

    // Check if both values are invalid numbers
    if (Number.isNaN(minValue) && Number.isNaN(maxValue)) {
      return {
        isValid: false,
        message: validationLabels?.invalidMinmax?.label?.[lang]
      };
    }

    // Check individual value validity
    if (Number.isNaN(minValue)) {
      return {
        isValid: false,
        message: validationLabels?.invalid?.label?.[lang]
      };
    }

    if (Number.isNaN(maxValue)) {
      return {
        isValid: false,
        message: validationLabels?.invalid?.label?.[lang]
      };
    }

    // Check logical relationship
    if (minValue > maxValue) {
      return {
        isValid: false,
        message: validationLabels?.minMax?.label?.[lang]?.(minValue, maxValue)
      };
    }

    return {
      isValid: true,
      message: ""
    };
  };

  /**
   * Validates input length against expected length
   * @param {HTMLElement} el - Input element to validate
   * @param {number} min - Minimum length
   * @param {number} max - Maximum length
   * @returns {Object} Validation result with isValid flag and message
   */
  const inputLength = (el, min, max) => {
    const isEnergyStar = (window.searchBy === "energystar") ? true : false;
    const inputValue = el?.value?.trim() || "";
   // Check if element exists and has a value
    if (!inputValue) {
      return {
        isValid: false,
        message: isEnergyStar
          ? validationLabels?.lengthNullEnergyStar?.label?.[lang]
          : validationLabels?.lengthNullAhri?.label?.[lang]
      };
    }

    const expectedLength = inputValue.length;

    // Energy Star: exactly 7 characters
    if (isEnergyStar) {
      if (expectedLength !== 7) {
        return {
          isValid: false,
          message: validationLabels?.lengthEnergyStar?.label?.[lang]?.()
        };
      }
    }
    // AHRI: between min and max characters
    else {
      if (expectedLength < min || expectedLength > max) {
        return {
          isValid: false,
          message: validationLabels?.lengthAHRI?.label?.[lang]?.(min, max)
        };
      }
    }
console.log(`Validation réussie pour la valeur d'entrée: ${inputValue}`);
    return {
      isValid: true
    };
  };

  /**
   * Validates min/max elements in the DOM
   * @returns {Object} Validation result with minmaxIsValid flag and itemsValidated array
   */
  const validateMinMax = () => {
    const minMaxElements = document.querySelectorAll('[data-validating="minmax"]');
    itemsValidated = []; // Reset validation array

    // Process each min/max element pair
    [...minMaxElements].forEach(el => {
      const minElement = el.querySelector("[class*='_min']");
      const maxElement = el.querySelector("[class*='_max']");

      if (minElement && maxElement) {
        const validation = minMax(minElement.value, maxElement.value);

        if (!validation.isValid) {
          itemsValidated.push({
            element: el,
            isValid: false,
            errorMessage: validation.message
          });
        }
      }
    });

    // Check if all items passed validation
    const minmaxIsValid = !itemsValidated.some(item => !item.isValid);

    return {
      minmaxIsValid,
      itemsValidated
    };
  };

  /**
   * Validates that selected brands don't exceed maximum allowed
   * @returns {Object} Validation result with isValid flag and optional message
   */
  const marquesMaxLength = () => {
    try {
      // Get selected items from the global marques selector
      const selectedItems = window.initMarquesSelector?.getSelectedMarques?.() ?? [];

      if (selectedItems.length > MAX_BRANDS_ALLOWED) {
        return {
          isValid: false,
          message: validationLabels?.lengthMarque?.label?.[lang]?.(MAX_BRANDS_ALLOWED)
        };
      }

      return {
        isValid: true
      };
    } catch (error) {
      console.warn("Error validating brands selection:", error);
      return {
        isValid: false,
        message: "Error validating brands selection"
      };
    }
  };

  // Public API
  return {
    inputLength,
    validateMinMax,
    marquesMaxLength,
    // Expose internal state for debugging (optional)
    get formIsValid() { return formIsValid; },
    set formIsValid(value) { formIsValid = Boolean(value); },
    get itemsValidated() { return [...itemsValidated]; }
  };
})();

// Expose to global scope
window.validationForm = validationForm;
export default validationForm;
